<template>
  <div
    class="products-by-categories"
    v-if="products.length">
    <div class="products-by-categories__background">
      <h3 class="products-by-categories__title">{{ title }}</h3>
      <div class="products-by-categories__wrapper">
        <CategoryFilter
          :data="filterData"
          @change-filter="handleChangeFilter"
          list />
        <ul
          ref="productsList"
          class="products-by-categories__products-list products-list"
          :class="{'custom-scrollbar': !isTouch}">
          <li
            class="products-list__item"
            v-for="(product, i) in computedProducts"
            :key="product.id">
            <ProductCard
              :id="product.id"
              :position="i + 1"
              :code="product.code.toString()"
              :title="product.title"
              :title-ru="product.title_ru"
              :category-id="product.category_id"
              :category-parent-id="product.category_parent_id"
              :link="product.link"
              :image-url="product.image_url"
              :second-image-url="product.second_image_url"
              :rating="product.rating"
              :reviews-count="product.reviews_count"
              :price="JSON.stringify(product.price)"
              :is-available="product.is_available"
              :is-novelty="product.is_novelty"
              :is-preorder="product.is_preorder"
              :is-in-wait-list="product.is_in_wait_list"
              :is-in-wish-list="product.is_in_wish_list"
              :badges="JSON.stringify(product.badges)"
              :bottom-badge="product.bottom_badge"
              :options="product.options"
              :characteristics="product.characteristics ? product.characteristics : []"
              :arrival-date="product.arrival_date"
              :arrival-date-employee="product.arrival_date_employee"
              :block="transliterate(titleRu)"
              :analytics-data="product.analytics_data"
              :available-in-store="product.available_only_in_store_data"
              :esputnik-data="product.esputnik_data"
              :esutnik-block-id="esputnikBlockId"
              :progressbar-total="product.progressbar_total"
              :progressbar-current="product.progressbar_current"
              slider
              not-adaptive
            />
          </li>
          <content-loader
            class="products-list__item"
            v-for="index in shownSkeletons"
            :key="index"
            :height="380"
            :width="278"
            :speed="2"
            primaryColor="#f3f3f3"
            secondaryColor="#ecebeb"
          >
            <rect
              x="30"
              y="10"
              rx="13"
              ry="13"
              width="220"
              height="220" />
            <rect
              x="20"
              y="271"
              rx="3"
              ry="3"
              width="238"
              height="13" />
            <rect
              x="20"
              y="290"
              rx="3"
              ry="3"
              width="123"
              height="13" />
            <rect
              x="20"
              y="337"
              rx="3"
              ry="3"
              width="90"
              height="24" />
          </content-loader>
          <li
            class="products-list__item products-list__item--link"
            v-if="currentCategory.link">
            <a
              class="products-list__link"
              :href="currentCategory.link">
              {{ $t('show_all_models') }}
            </a>
          </li>
        </ul>
      </div>
      <a
        class="products-by-categories__link"
        :href="currentCategory.link">
        {{ $t('show_all_models') }}
      </a>
    </div>
  </div>
</template>

<script>
import { isTouch } from '@/mixins/isTouchDevice'
import { transliteration } from '@/mixins/transliteration'
import { ContentLoader } from 'vue-content-loader'
import ProductCard from '@/components/product/product-card/ProductCard'
import CategoryFilter from './CategoryFilter'
import api from '@/api'

export default {
  name: 'ProductsByCategories',
  components: { CategoryFilter, ProductCard, ContentLoader },
  mixins: [transliteration, isTouch],
  data () {
    return {
      currentCategory: {
        id: null,
        link: ''
      },
      loadedInfo: this.dataForFixedBlock,
      loadedProducts: this.products,
      isLoading: false,
      shownSkeletons: 0
    }
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    titleRu: {
      type: String,
      default: ''
    },
    link: {
      type: String,
      default: ''
    },
    linkTitle: {
      type: String,
      default: ''
    },
    products: {
      type: Array,
      default: () => []
    },
    showFilter: {
      type: Boolean,
      default: false
    },
    filterData: {
      type: Array,
      default: () => []
    },
    dataForFixedBlock: {
      type: Object,
      default: () => {}
    },
    isServerPagination: {
      type: Boolean,
      default: false
    },
    esputnikBlockId: String
  },
  computed: {
    computedProducts () {
      if (this.isServerPagination) {
        return this.loadedProducts.slice(0, 7)
      }

      return this.products.filter(product => product.category_id === this.currentCategory.id).slice(0, 7)
    }
  },
  methods: {
    handleChangeFilter (item) {
      const { productsList } = this.$refs
      productsList.scrollLeft = 0

      this.currentCategory = item
    },
    loadMoreProducts () {
      this.isLoading = true

      api.blocks.getFixedBlockProducts({
        id: this.id,
        offset: this.loadedInfo?.offset,
        categoryId: this.currentCategory.id
      }).then(res => {
        this.isLoading = false

        if (res.success) {
          this.loadedInfo = res
          this.loadedProducts = [...this.loadedProducts, ...res.data]
        }
      })
    }
  },
  watch: {
    'currentCategory.id' () {
      if (this.isServerPagination) {
        this.loadedInfo = {}
        this.loadedProducts = []
        this.loadMoreProducts()
      }
    },
    isLoading (value) {
      if (value) {
        this.shownSkeletons = 10
      } else {
        this.shownSkeletons = 0
      }
    }
  }
}
</script>

<style scoped lang="sass">

.products-by-categories

  +mmin($tablet--hor)
    padding: 0 15px

.products-by-categories__background

  +mmin($tablet--hor)
    background-color: #fff

.products-by-categories__wrapper

  +mmin($tablet--hor)
    display: flex

.products-by-categories__title
  display: inline-block
  font-size: 18px
  font-weight: 500
  text-transform: uppercase
  margin: 30px 0
  padding: 0 15px

  +mmin($tablet--hor)
    font-size: 24px
    margin: 40px 0 35px
    padding: 0 40px

.products-by-categories__link
  display: block
  width: 100%
  text-align: center
  margin: 40px auto
  max-width: max-content

  +mmin($tablet--hor)
    display: none

.products-list
  +list-unstyled
  display: flex
  flex-flow: row nowrap
  overflow: auto

  +mmin($tablet--hor)
    width: 75%
    min-height: 712px
    flex-flow: row wrap
    align-content: flex-start
    overflow: visible
    border-top: 1px solid #f3f3f3
    border-left: 1px solid #f3f3f3

.products-list__item
  width: 300px
  flex-shrink: 0
  height: max-content
  border-right: 1px solid #F3F3F3

  +mmin($tablet--hor)
    width: 33.333%
    border-bottom: 1px solid #f3f3f3

  +mmin($large--ext)
    width: 25%

  &--link
    display: none

    +mmin($tablet--hor)
      display: flex
      justify-content: center
      align-items: center
      height: 355px

.products-list__link,
.products-by-categories__link
  font-size: 16px
  font-weight: 500
  color: $nd-link-color
  border-bottom: 1px solid $nd-link-color

  &:hover
    border-color: transparent
</style>
